import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

import workoutRecordsService from '../../../services/workoutRecordsService';

const PRBadge = ({ label }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500/15 text-yellow-500 border border-yellow-500/30">
    <Icon name="Trophy" size={10} />
    PR
  </span>
);

const StatCard = ({ icon, label, value, unit, date }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border/30">
      <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg flex-shrink-0">
        <Icon name={icon} size={16} className="text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold text-foreground">
          {value} <span className="text-xs font-normal text-muted-foreground">{unit}</span>
        </p>
      </div>
      {date && (
        <p className="text-xs text-muted-foreground flex-shrink-0">
          {new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
        </p>
      )}
    </div>
  );
};

const ExercisePRCard = ({ pr }) => {
  const [expanded, setExpanded] = useState(false);

  const hasAnyPR = pr?.bestWeight || pr?.bestReps || pr?.bestDistance || pr?.bestDuration || pr?.bestCalories;
  if (!hasAnyPR) return null;

  return (
    <motion.div
      layout
      className="bg-card rounded-xl border border-border/40 overflow-hidden shadow-wellness elevation-1"
    >
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/20 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 bg-yellow-500/10 rounded-lg">
            <Icon name="Trophy" size={18} className="text-yellow-500" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-foreground capitalize">{pr?.exerciseName}</p>
            <p className="text-xs text-muted-foreground">{pr?.totalSessions} session{pr?.totalSessions !== 1 ? 's' : ''} logged</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PRBadge label="PR" />
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {pr?.bestWeight && (
                <StatCard
                  icon="Dumbbell"
                  label="Best Weight"
                  value={pr?.bestWeight?.value}
                  unit={`kg × ${pr?.bestWeight?.sets}×${pr?.bestWeight?.reps}`}
                  date={pr?.bestWeight?.date}
                />
              )}
              {pr?.bestReps && (
                <StatCard
                  icon="Repeat"
                  label="Best Reps"
                  value={pr?.bestReps?.value}
                  unit={`reps (${pr?.bestReps?.sets} sets)`}
                  date={pr?.bestReps?.date}
                />
              )}
              {pr?.bestDistance && (
                <StatCard
                  icon="MapPin"
                  label="Best Distance"
                  value={pr?.bestDistance?.value}
                  unit="km"
                  date={pr?.bestDistance?.date}
                />
              )}
              {pr?.bestDuration && (
                <StatCard
                  icon="Clock"
                  label="Best Duration"
                  value={pr?.bestDuration?.value}
                  unit="min"
                  date={pr?.bestDuration?.date}
                />
              )}
              {pr?.bestCalories && (
                <StatCard
                  icon="Flame"
                  label="Best Calories"
                  value={pr?.bestCalories?.value}
                  unit="cal"
                  date={pr?.bestCalories?.date}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const PersonalRecordsPanel = ({ userId }) => {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await workoutRecordsService?.getPersonalRecords(userId);
        setPrs(data);
      } catch (err) {
        console.error('Error loading PRs:', err);
        setError('Could not load personal records.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const filtered = prs?.filter(pr =>
    pr?.exerciseName?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div className="bg-card rounded-xl shadow-wellness elevation-1 border border-border/40 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-500/10 rounded-lg">
              <Icon name="Trophy" size={20} className="text-yellow-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Personal Records</h3>
              <p className="text-sm text-muted-foreground">All-time bests per exercise</p>
            </div>
          </div>
          <span className="text-sm font-medium text-muted-foreground bg-muted/40 px-3 py-1 rounded-full">
            {prs?.length} exercise{prs?.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Search */}
        {prs?.length > 3 && (
          <div className="mt-4 relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search exercise..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-muted/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-8 text-muted-foreground text-sm">{error}</div>
        )}

        {!loading && !error && filtered?.length === 0 && (
          <div className="text-center py-12">
            <div className="flex items-center justify-center w-14 h-14 bg-yellow-500/10 rounded-full mx-auto mb-3">
              <Icon name="Trophy" size={28} className="text-yellow-500/50" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No records yet</p>
            <p className="text-xs text-muted-foreground">
              {search ? 'No exercises match your search.' : 'Log your first workout below to start tracking PRs.'}
            </p>
          </div>
        )}

        {!loading && !error && filtered?.map((pr) => (
          <ExercisePRCard key={pr?.exerciseName} pr={pr} />
        ))}
      </div>
    </div>
  );
};

export default PersonalRecordsPanel;
